import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { User, Mail, Briefcase, FileText } from "lucide-react";

interface UserProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    name: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName: string;
    title: string;
    bio: string;
  } | null;
}

const UserProfileModal = ({
  open,
  onOpenChange,
  user,
}: UserProfileModalProps) => {
  const { t } = useTranslation();

  if (!user) return null;

  const fullName = user.displayName || `${user.firstName} ${user.lastName}`.trim() || user.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <User className="w-5 h-5 text-primary" />
            {t("user_profile") || "User Profile"}
          </DialogTitle>
          <DialogDescription>
            {t("view_user_details") || "View user details and information"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name Section */}
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 flex-shrink-0 mt-0.5">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-500 mb-1">
                {t("name") || "Name"}
              </p>
              <p className="text-base font-semibold text-gray-900">
                {fullName}
              </p>
            </div>
          </div>

          {/* Email Section */}
          {user.email && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/5 flex-shrink-0 mt-0.5">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t("email") || "Email"}
                </p>
                <p className="text-base text-gray-900 break-all">
                  {user.email}
                </p>
              </div>
            </div>
          )}

          {/* Title Section */}
          {user.title && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-50 flex-shrink-0 mt-0.5">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t("title") || "Title"}
                </p>
                <p className="text-base text-gray-900">
                  {user.title}
                </p>
              </div>
            </div>
          )}

          {/* Bio Section */}
          {user.bio && (
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 flex-shrink-0 mt-0.5">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 mb-2">
                  {t("bio") || "Bio"}
                </p>
                <div
                  className="text-sm leading-relaxed text-gray-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: user.bio }}
                />
              </div>
            </div>
          )}

          {/* Empty state if no additional info */}
          {!user.title && !user.bio && (
            <div className="py-4 text-center text-sm text-gray-500">
              {t("no_additional_info") || "No additional information available"}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("close") || "Close"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
